<template>
    <div class="radio-wrapper">
        <div
        class="input-elements-wrapper"
        :class="{ 'disabled': disabled, 'with-tooltips': tooltipInfo }"
        @click.stop="selected ? null : emit('clicked', id)">
            <!-- *********************************************
            *                      Radio                     *
            ********************************************** -->
            <div
            class="input-container"
            :class="{
                'disabled': disabled,
                'error-state': errorState,
                'selected': selected
            }">
                <input
                type="radio"
                :class="{ 'disabled': disabled, 'selected': selected }"
                :id="`radio-${id}`"
                :value="value"
                :disabled="disabled"
                :checked="selected">
            </div>
            <!-- *********************************************
            *                      Label                     *
            ********************************************** -->
            <label-generic
            v-if="text"
            :preventLabelAutoCapitalize="preventLabelAutoCapitalize"
            :tooltipInfo="tooltipInfo"
            :for="`radio-${id}`"
            :class="{ 'disabled': disabled, 'selected': selected, 'selected-disabled': disabled && selected }"
            :text="text"/>
            <!-- *********************************************
            *                      Icon                      *
            ********************************************** -->
            <icon v-if="hasIcon"/>
        </div>
        <!-- *********************************************
        *                  Error Message                 *
        ********************************************** -->
        <div v-if="errorMessage && errorState && !selected" class='error-message'>{{ errorMessage }}</div>
        <slot></slot>
    </div>
</template>

<script setup>
import LabelGeneric from '@/components/generic/labels/Generic'

/* Vue */
import { computed, defineAsyncComponent } from 'vue'

/* Assets */

/* Emmitters */
const emit = defineEmits(['clicked'])
/** Props received
@id { String, Number } - radio id
@text { String } - text
@value { String } - radio value
@disabled { Boolean } - component is disabled or not
@selected { Boolean } - radio is selected or not
@errorState { Boolean } - property to show if radio has error or not
@errorMessage { String }  - error message to display
@checkStyle { Boolean } - when the radio is checked, it returns the same icon as used in the checkbox
@preventLabelAutoCapitalize {Boolean} prop to prevent label from capitalizing text
@hasIcon { Boolean }
@iconPath { String } path to the icon
@tooltipInfo {Object} with:
	- title {String} - tooltip title
	- text {String} - tooltip text
	- description {String} - tooltip description
	- negative {Boolean} - prop to apply negative styles
	- size {String} can be [medium, small, tiny (default)]
	- leftButtonText {String} - text for left button, has default text if this is not passed
	- rightButtonText {String} - text for right button, has default text if this is not passed
	- applyPositionFixed {Boolean} - prop to force tooltipo to use fixed position
@tooltipIcon {String} - icon name to trigger tooltip
*/
const props = defineProps({
    id: String || Number,
    text: String,
    value: String,
    disabled: Boolean,
    selected: Boolean,
    errorState: Boolean,
    errorMessage: String,
    checkStyle: Boolean,
    preventLabelAutoCapitalize: Boolean,
    hasIcon: Boolean,
    iconPath: String,
	tooltipInfo: Object,
	tooltipIcon: String
})

/* `**` glob (not the dynamicImportToGlob plugin) because iconPath crosses a folder. */
const assetModules = import.meta.glob('/src/assets/**/*.svg')

const icon = computed(() => {
    return defineAsyncComponent(assetModules[`/src/assets/${props.iconPath}.svg`])
})
</script>

<style lang="scss" scoped>
.radio-wrapper {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    row-gap: 8px;

    .input-elements-wrapper {
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;

        &.disabled {
            pointer-events: none;
        }

        .input-container {
            display: flex;
            justify-content: center;
            align-items: center;

            position: relative;
            border-radius: 50%;
            background-color: var(--secondary-light-1);
            border: 1px solid var(--secondary-light-1);
            cursor: pointer;

            &.disabled {
                opacity: 0.4;
                pointer-events: none;
            }

            @media (hover: hover) and (pointer: fine) {
                &:hover {
                    background-color: var(--secondary);
                    border: 1px solid var(--secondary);
                }
            }

            &:active {
                background-color: var(--secondary-dark-1);
                border: 1px solid var(--secondary-dark-1);
            }

            &.error-state {
                background-color: var(--secondary-light-3);
                border: 1px solid var(--negative);

                @media (hover: hover) and (pointer: fine) {
                    &:hover {
                        background-color: var(--button-secondary-state-hover);
                    }
                }

                &:active {
                    background-color: var(--button-secondary-state-press);
                }
            }

            &.selected {
                display: flex;
                align-items: center;
                justify-content: center;

                background-color: var(--primary);
                border: 1px solid var(--primary);
                position: relative;

                &::after {
                    content: '';
                    width: 40%;
                    height: 40%;

                    background-color: var(--secondary-light-4);
                    position: absolute;
                    border-radius: 50%;
                }

                &.disabled {
                    opacity: 0.5;
                }
            }
        }

        :deep(.label-container) {
            @include H7-Regular;
            line-height: 16px;
            color: var(--secondary-dark-3);
            cursor: pointer;

            :deep(a) {
                color: var(--primary);
            }

            &.selected {
                @include H7-Medium;
                color: var(--primary);
                cursor: default;
            }

            &.disabled {
                color: var(--secondary-dark-3);
                opacity: 0.5;
                cursor: default;
                pointer-events: none;
            }

            &.selected-disabled {
                color: var(--primary);
                opacity: 0.5;
                cursor: default;
            }
        }

        &.with-tooltips {

            :deep(label) {
                line-height: 22px;

                .slot {
                    .icon {
                        transform: translateY(4px) translateX(-2px);
                    }
                }
            }
        }

        input {
            width: 20px;
            height: 20px;
            flex-shrink: 0;

            position: relative;
            appearance: none;

            cursor: pointer;

            &.selected {
                cursor: default;
            }

            &.disabled {
                opacity: 0.5;
                pointer-events: none;
                cursor: default;
            }
        }
    }

    .error-message {
        @include H7-Regular;
        line-height: 16px;
        color: var(--negative);
    }
}
</style>
